import { useContext, useState } from 'react'
import { useFirestoreGeneral } from '../../firebase/useFirestore'
import { client } from '../../hooks/Client'
import { SavedIcon } from '../../StateManagment/SavedIcon'
import { Auth } from '../../StateManagment/Auth'
import applyQualifyingToggle from '../../helpers/questionnaire/applyQualifyingToggle'
import { isInQualifyingSet } from '../../helpers/questionnaire/isQualifyingQuestion'

// Eén schakelaar per set kwalificeringsvragen, in 'Wie doet mee'.
//
// Nodig naast `ToggleButton` omdat die één veld op één document schrijft. Deze
// schakelaar heeft geen eigen veld: hij staat aan als de VRAGENLIJST de vragen
// heeft, en zetten schrijft vragen, opties en meetmomenten in één batch.
//
// Niets tonen zolang de vragen of onderzoeken laden: 'uit' lezen omdat de
// vragen nog niet binnen zijn, is een onware bewering, en een klik zou dan een
// tweede set aanmaken.
// AI-CONTEXT: See docs/pitfalls.md#state-gate-defaults-open-while-loading
// AI-CONTEXT: See docs/features/sector-kwalificeringsvragen.md

/**
 * @param {Array<string>} titles
 * @returns {string} "'A'", "'A' en 'B'", "'A', 'B' en 'C'"
 */
const joinTitles = (titles) => {
    const quoted = titles.map((t) => `'${t}'`)
    return quoted.length <= 1 ? quoted.join('') : `${quoted.slice(0, -1).join(', ')} en ${quoted[quoted.length - 1]}`
}

/**
 * @param {object} props
 * @param {{ID: string, docid: string, QuestionnaireID?: string}} props.research - Het onderzoek in de bouwer
 * @param {import('../../helpers/questionnaire/sectorQualifyingQuestions').QualifyingSet} props.set -
 *   De set kwalificeringsvragen waar deze schakelaar over gaat
 */
const QualifyingQuestionsRow = ({ research, set }) => {
    const [, setSaved] = useContext(SavedIcon)
    const [auth] = useContext(Auth)
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState('')

    const questionnaireId = research?.QuestionnaireID || ''
    const fields = useFirestoreGeneral('QuestionnaireFields', 'QuestionnaireID', questionnaireId)
    const researches = useFirestoreGeneral('Research', 'QuestionnaireID', questionnaireId)

    const title = <p><b>{set.label}</b></p>
    const text = <p>Drie vaste vragen die bepalen of een huishouden in aanmerking komt. Ze staan bovenaan de vragenlijst en worden alleen op het eerste meetmoment gesteld.</p>

    if (!questionnaireId) {
        return (
            <div className='reasearch-builder-option-container'>
                <div className='research-builder-options-title-container'>{title}{text}</div>
                <p>Kies eerst een vragenlijst.</p>
            </div>
        )
    }

    if (!Array.isArray(fields) || !Array.isArray(researches)) return null
    // Na het kiezen van een andere vragenlijst houdt de listener nog even de
    // velden van de vorige vast; dan is de stand nog niet bekend.
    if (fields.some((f) => f.QuestionnaireID && f.QuestionnaireID !== questionnaireId)) return null

    const isOn = fields.some((f) => isInQualifyingSet(f, set))
    const others = researches.filter((r) => r.ID !== research.ID).map((r) => r.Title)

    const toggleHandler = async () => {
        if (busy) return
        setBusy(true)
        setError('')
        try {
            await applyQualifyingToggle({ set, active: !isOn, questionnaireId, client, userId: auth?.ID || '' })
            setSaved('flex')
        } catch (err) {
            setError(`Opslaan is niet gelukt: ${err.message}`)
        } finally {
            setBusy(false)
        }
    }

    return (
        <div className='reasearch-builder-option-container'>
            <div className='research-builder-options-title-container'>
                {title}
                {text}
                {others.length > 0 && (
                    <p className='research-builder-option-note'>
                        Deze vragenlijst hoort ook bij {joinTitles(others)}. Daar gelden de vragen ook.
                    </p>
                )}
                {error && <p className='research-builder-option-error'>{error}</p>}
            </div>
            <div className='toggle-container'>
                <label className='toggle-switch'>
                    <input type='checkbox' aria-label={set.label} checked={isOn} disabled={busy} onChange={toggleHandler} />
                    <span className='slider'></span>
                </label>
            </div>
        </div>
    )
}

export default QualifyingQuestionsRow
